import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00ffff',
      light: '#4dffff',
      dark: '#00b3b3',
    },
    secondary: {
      main: '#ff6b35',
      light: '#ff8c69',
      dark: '#e55100',
    },
    background: {
      default: '#0a0a0a',
      paper: '#111111',
    },
    text: {
      primary: '#00ff41',
      secondary: '#00ffff',
    },
    success: {
      main: '#00ff41',
    },
    warning: {
      main: '#ffab00',
    },
    error: {
      main: '#ff1744',
    },
    grey: {
      900: '#0a0a0a',
      800: '#111111',
      700: '#1a1a1a',
      600: '#333333',
      500: '#666666',
      400: '#999999',
      300: '#cccccc',
    },
  },
  typography: {
    fontFamily: '"Fira Code", "JetBrains Mono", "Cascadia Code", "Monaco", "Menlo", monospace',
    h1: {
      fontWeight: 700,
      fontSize: '3.5rem',
      lineHeight: 1.2,
      fontFamily: '"Fira Code", monospace',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2.5rem',
      lineHeight: 1.3,
      fontFamily: '"Fira Code", monospace',
    },
    h3: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.4,
      fontFamily: '"Fira Code", monospace',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
      fontFamily: '"Fira Code", monospace',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.25rem',
      lineHeight: 1.5,
      fontFamily: '"Fira Code", monospace',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      fontFamily: '"Inter", sans-serif',
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(145deg, #111111, #1a1a1a)',
          border: '1px solid #00ffff30',
          boxShadow: '0 8px 32px rgba(0, 255, 255, 0.1)',
          '&:hover': {
            border: '1px solid #00ffff80',
            boxShadow: '0 8px 32px rgba(0, 255, 255, 0.3), inset 0 1px 0 rgba(0, 255, 255, 0.1)',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 4,
          fontFamily: '"Fira Code", monospace',
          background: 'linear-gradient(145deg, #111111, #1a1a1a)',
          border: '1px solid #00ffff50',
          transition: 'all 0.3s ease',
          '&:hover': {
            background: 'linear-gradient(145deg, #1a1a1a, #222222)',
            border: '1px solid #00ffff',
            boxShadow: '0 0 20px rgba(0, 255, 255, 0.3)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(145deg, #111111, #1a1a1a)',
          border: '1px solid #00ffff30',
          fontFamily: '"Fira Code", monospace',
          fontSize: '0.75rem',
          '&:hover': {
            background: 'linear-gradient(145deg, #1a1a1a, #222222)',
            border: '1px solid #00ffff80',
            boxShadow: '0 0 10px rgba(0, 255, 255, 0.2)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(145deg, #111111, #1a1a1a)',
          border: '1px solid #00ffff20',
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  </React.StrictMode>
); 